import React, { useState, useEffect } from 'react';
import { Calendar, Image, ChevronLeft, X } from 'lucide-react';
import PhotoLightbox from '../PhotoLightbox';
import { OptimizedImage } from '../ui/optimized-image';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MemberAlbums = () => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/member/albums`);
      if (response.ok) {
        const data = await response.json();
        setAlbums(data.albums || []);
      }
    } catch (error) {
      console.error('Erreur chargement albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbumDetails = async (albumId) => {
    try {
      const response = await fetch(`${API_BASE}/api/member/albums/${albumId}`);
      if (response.ok) {
        const album = await response.json();
        setSelectedAlbum(album);
      }
    } catch (error) {
      console.error('Erreur chargement album:', error);
    }
  };

  const handlePhotoClick = (index) => {
    setSelectedPhotoIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  // Vue détaillée d'un album
  if (selectedAlbum) {
    const photos = selectedAlbum.photos || [];
    const photoUrls = photos.map(p => `${API_BASE}${p.photo_url}`);

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <button
              onClick={() => setSelectedAlbum(null)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour aux albums
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Lora, serif', color: 'rgb(196, 150, 96)' }}>
              {selectedAlbum.titre}
            </h1>
            <p className="text-gray-600">
              <Calendar className="inline w-4 h-4 mr-1" />
              {new Date(selectedAlbum.date_album).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            <p className="text-gray-500 text-sm mt-1">
              {photos.length} photo{photos.length > 1 ? 's' : ''}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div
                key={photo.photo_id}
                onClick={() => handlePhotoClick(index)}
                className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all h-64"
              >
                <OptimizedImage
                  src={`${API_BASE}${photo.photo_url}`}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  containerClassName="w-full h-full"
                  useThumbnail={true}
                  placeholder="skeleton"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all pointer-events-none"></div>
              </div>
            ))}
          </div>

          {photos.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Aucune photo dans cet album</p>
            </div>
          )}

          {lightboxOpen && (
            <PhotoLightbox
              photos={photoUrls}
              currentIndex={selectedPhotoIndex}
              onClose={() => setLightboxOpen(false)}
              onNavigate={(index) => setSelectedPhotoIndex(index)}
            />
          )}
        </div>
      </div>
    );
  }

  // Vue grille des albums
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Lora, serif', color: 'rgb(196, 150, 96)' }}>
            Albums Photos
          </h1>
          <p className="text-gray-600" style={{ fontFamily: 'Lora, serif' }}>
            Découvrez nos souvenirs en images
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {albums.map((album) => {
            const coverUrl = album.photo_couverture_url || '/placeholder-album.jpg';

            return (
              <div
                key={album.id}
                onClick={() => fetchAlbumDetails(album.id)}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
              >
                {album.photo_couverture_url ? (
                  <div className="relative overflow-hidden h-64">
                    <OptimizedImage
                      src={`${API_BASE}${coverUrl}`}
                      alt={album.titre}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      containerClassName="w-full h-full"
                      useThumbnail={true}
                      placeholder="skeleton"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all pointer-events-none"></div>
                  </div>
                ) : (
                  <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                    <Image className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Lora, serif', color: 'rgb(196, 150, 96)' }}>
                    {album.titre}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2" style={{ fontFamily: 'Lora, serif' }}>
                    <Calendar className="inline w-4 h-4 mr-1" />
                    {new Date(album.date_album).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-sm text-gray-500" style={{ fontFamily: 'Lora, serif' }}>
                    {album.photo_count} photo{album.photo_count > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {albums.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p style={{ fontFamily: 'Lora, serif' }}>Aucun album disponible pour le moment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberAlbums;
